import Seo, { pageSeo } from "../components/Seo";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ExternalLink, MessageSquare, Calculator } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PageBanner, Reveal, SectionTitle } from "../components/Shared";
import ContactForm from "../components/ContactForm";
import QuoteForm from "../components/QuoteForm";
import { company, facilityPhotos, products } from "../mock";

const infoCards = [
  { icon: Phone, title: "Call Us", lines: [company.phoneDisplay, company.phone2Display], href: `tel:${company.phone}` },
  { icon: Mail, title: "Email Us", lines: [company.email], href: `mailto:${company.email}` },
  { icon: MapPin, title: "Visit Us", lines: company.addressLines, href: company.mapsUrl },
  { icon: Clock, title: "Working Hours", lines: [company.hours, "Sunday: Closed"] },
];

const Contact = () => {
  const { hash, search } = useLocation();
  const [tab, setTab] = useState(hash === "#quote" ? "quote" : "general");
  const presetId = new URLSearchParams(search).get("product");
  const presetProduct = products.find((p) => p.id === presetId)?.title || "";
  const presetShade = new URLSearchParams(search).get("shade") || "";

  useEffect(() => {
    if (hash === "#quote") {
      setTab("quote");
      setTimeout(() => document.getElementById("forms")?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    }
  }, [hash]);

  return (
  <div data-testid="contact-page">
    <Seo {...pageSeo.contact} />
    <PageBanner title="Contact Us" subtitle="Sales queries, technical questions, bulk or emergency orders - we are one call away." image={facilityPhotos.companySide.src} crumbs={["Contact"]} />

    <section className="section bg-white">
      <div className="container-dic">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 -mt-24 relative z-20">
          {infoCards.map(({ icon: Icon, title, lines, href }, i) => (
            <Reveal key={title} delay={i * 80} className="card-plain">
              <div className="industry-icon mx-0 w-12 h-12"><Icon size={20} /></div>
              <h6 className="mt-4 text-[15px] font-bold text-[#231f20]">{title}</h6>
              <div className="mt-1.5 text-[13.5px] leading-6 text-[#58595b] break-words">
                {lines.map((l) => <div key={l}>{l}</div>)}
              </div>
              {href && (
                <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="inline-flex items-center gap-1 mt-3 text-[12px] font-bold uppercase tracking-wide text-[#7ab929] hover:text-[#5f9a17]">
                  {title === "Visit Us" ? "Open in Maps" : title === "Email Us" ? "Write to us" : "Call now"} <ExternalLink size={12} />
                </a>
              )}
            </Reveal>
          ))}
        </div>

        <div className="grid lg:grid-cols-5 gap-12 mt-20">
          <Reveal className="lg:col-span-3">
            <div id="forms" className="scroll-mt-32">
              <p className="eyebrow">Reach Us</p>
              <h2 className="section-title text-left">Send us your requirement</h2>
              <p className="mt-3 text-[15px] leading-7 text-[#58595b]">
                Ask a question or request a priced quote by telling us the product, quantity and shade. When you press Send, your enquiry is
                saved and forwarded to our team on WhatsApp and email, and we respond within one working day.
              </p>
              <Tabs value={tab} onValueChange={setTab} className="mt-8">
                <TabsList className="h-auto p-1 bg-[#f1f1f1] rounded-md w-full grid grid-cols-2">
                  <TabsTrigger value="general" className="py-2.5 text-[14px] font-bold data-[state=active]:bg-white data-[state=active]:text-[#5f9a17] data-[state=active]:shadow-sm" data-testid="tab-general">
                    <MessageSquare size={15} className="mr-2" /> General Enquiry
                  </TabsTrigger>
                  <TabsTrigger value="quote" className="py-2.5 text-[14px] font-bold data-[state=active]:bg-white data-[state=active]:text-[#5f9a17] data-[state=active]:shadow-sm" data-testid="tab-quote">
                    <Calculator size={15} className="mr-2" /> Request a Quote
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="general" className="mt-4">
                  <div className="card-plain">
                    <ContactForm variant="light" compact source="contact" />
                  </div>
                </TabsContent>
                <TabsContent value="quote" className="mt-4">
                  <div className="card-plain">
                    <QuoteForm presetProduct={presetProduct} presetShade={presetShade} />
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-2" >
            <div id="locate" className="scroll-mt-32">
              <p className="eyebrow">Locate Our Factory</p>
              <h3 className="text-[22px] font-bold text-[#231f20]">Medchal, Telangana</h3>
              <div className="mt-5 rounded-md overflow-hidden shadow-md border border-[#eee]">
                <iframe
                  title="Anjaneya Inks and Coating location"
                  src="https://www.google.com/maps?q=Medchal%20Village%2C%20Medchal%20Mandal%2C%20Telangana%20501401&output=embed"
                  width="100%"
                  height="320"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
              <div className="mt-5 text-[14px] leading-7 text-[#58595b]">
                <p className="font-bold text-[#231f20]">{company.name}</p>
                {company.addressLines.map((l) => <p key={l}>{l}</p>)}
              </div>
              <img src={facilityPhotos.mainGate.src} alt={facilityPhotos.mainGate.alt} className="mt-6 h-44 w-full object-cover rounded-md shadow-md" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </div>
  );
};

export default Contact;
